import { useState } from 'react';
import { authenticatedFetch } from '@/utils/api';

interface CreateCollectionModalProps {
  onClose: () => void;
  onCreateSuccess: (collection: any) => void;
}

export default function CreateCollectionModal({ onClose, onCreateSuccess }: CreateCollectionModalProps) {
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Please enter a name for the collection.');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await authenticatedFetch('/api/collections', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          is_private: true
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error creating collection');
      }

      const data = await response.json();
      const newCollection = data.data;
      
      onCreateSuccess(newCollection);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error creating collection. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md transform transition-all">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Create New Collection</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="space-y-4">
              {/* Collection name input field */}
              <div>
                <label htmlFor="collection-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Collection Name
                </label>
                <input 
                  type="text" 
                  id="collection-name" 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., My Marketing Prompts" 
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:border-transparent"
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Error message */}
              {error && (
                <p className="text-red-600 text-sm">{error}</p>
              )}

              {/* Primary action button */}
              <button 
                type="submit"
                className="w-full bg-brand-dark-blue text-white font-semibold py-2 px-4 rounded-md hover:bg-brand-electric-blue transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create Collection'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}